import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Check, X } from 'lucide-react';
// The api client AND the i18n module are imported LAZILY, and that is load-bearing rather than a style choice: <T> ends
// up inside the shared UI primitives, so a top-level `import { api }` here drags the Supabase client
// into every module that merely renders a label — which broke 19 test suites at once. The Supabase
// client throws at import time when env vars are absent, and lib/i18n runs i18next.init() on
// import, which explodes in every suite that mocks react-i18next. Nothing about SHOWING a phrase
// needs either one; only editing one does.
const getApi = () => import('../../lib/api').then((m) => m.api);
const getI18n = () => import('../../lib/i18n');

// Edit the wording where you READ it. Migration 067; the whole-file editor lives at /admin/text.
//
// Ali, 2026-08-15: "when the button hit, all the phrases on page becomes editable and with a small
// pen/edit button next to each phrases."
//
// WHY A COMPONENT AND NOT A DOM SCANNER. Once the lookup runs, only the words reach the screen — the key
// is gone, and a pen needs the key to know what it is saving. Scanning the DOM and matching by text
// would misfire the moment two phrases share wording, and would save the wrong key silently. So the
// key travels with the phrase: the T component renders the text and, in edit mode, the pen beside it.
//
// ATTRIBUTES ARE A REAL LIMIT, stated rather than hidden. A placeholder or a title can only hold
// plain characters, so no pen can live inside one. Those keys are edited from /admin/text, and
// `useEditableAttr` marks the surrounding field so the pen can sit next to it instead.

const TextEditContext = createContext({ editing: false, setEditing: () => {}, isSuper: false });

export function useTextEdit() {
  return useContext(TextEditContext);
}

export function TextEditProvider({ children }) {
  const [editing, setEditing] = useState(false);
  const [isSuper, setIsSuper] = useState(false);

  useEffect(() => {
    let alive = true;
    getApi()
      .then((api) => api.getMe())
      .then((r) => { if (alive) setIsSuper(!!r.is_admin); })
      .catch(() => { /* not an admin, or not signed in: no edit mode, no noise */ });
    return () => { alive = false; };
  }, []);

  // Leaving edit mode when the admin navigates away keeps a stray pen from following them onto a
  // page they only meant to look at.
  const value = useMemo(
    () => ({ editing: isSuper && editing, setEditing, isSuper }),
    [editing, isSuper],
  );
  return <TextEditContext.Provider value={value}>{children}</TextEditContext.Provider>;
}

/** The toggle. Renders nothing at all for a non-superadmin — this changes what every user reads. */
export function TextEditToggle() {
  const { t } = useTranslation();
  const { editing, setEditing, isSuper } = useTextEdit();
  if (!isSuper) return null;
  return (
    <button
      type="button"
      onClick={() => setEditing((v) => !v)}
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border transition-colors ${
        editing
          ? 'bg-warning-soft text-warning border-warning-border'
          : 'bg-card text-muted-foreground border-border hover:text-foreground'
      }`}
      data-testid="text-edit-toggle"
    >
      <Pencil className="h-3.5 w-3.5" />
      {editing ? t('textEdit.done') : t('textEdit.edit')}
    </button>
  );
}

/**
 * One editable phrase.
 *
 * Outside edit mode this is exactly the text — no wrapper that could disturb layout, no pen, and no
 * cost. That matters: this ends up around every phrase in the product, and the 99% case is a user
 * who is not editing anything.
 */
export function T({ k, values, as: As = 'span', className }) {
  const { t } = useTranslation();
  const { editing } = useTextEdit();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const text = t(k, values);

  const save = useCallback(async () => {
    setBusy(true);
    try {
      const [api, i18nMod] = await Promise.all([getApi(), getI18n()]);
      const lang = i18nMod.default.language;
      await api.uiText('set', { lang, key: k, value: draft });
      // Apply straight away so the phrase changes under the pen that changed it. A save you cannot
      // see reads as a save that did not work.
      i18nMod.applyTextOverrides(lang, { [k]: draft });
      setOpen(false);
      toast.success(t('textEdit.saved'));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [k, draft, t]);

  if (!editing) return <>{text}</>;

  if (open) {
    return (
      <span className="inline-flex items-center gap-1 align-middle">
        <input
          className="text-sm border border-warning rounded px-1.5 py-0.5 bg-card text-foreground min-w-[12rem]"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setOpen(false);
          }}
          data-testid={`text-edit-input-${k}`}
        />
        <span role="button" tabIndex={0} onClick={busy ? undefined : save}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          className="text-success cursor-pointer" data-testid={`text-edit-save-${k}`} title={t('textEdit.save')}>
          <Check className="h-3.5 w-3.5" />
        </span>
        <span role="button" tabIndex={0} onClick={() => setOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Enter') setOpen(false); }}
          className="text-muted-foreground cursor-pointer" data-testid={`text-edit-cancel-${k}`} title={t('textEdit.cancel')}>
          <X className="h-3.5 w-3.5" />
        </span>
      </span>
    );
  }

  return (
    <As className={`${className || ''} outline-dashed outline-1 outline-warning/40 rounded-sm`} data-i18n-key={k}>
      {text}
      {/* A SPAN, not a button, and that is not cosmetic: <T> wraps phrases that already live inside
          buttons — the sidebar brand is one — and a <button> inside a <button> is invalid HTML that
          React refuses to render, taking the whole page down the moment edit mode is switched on.
          Measured 2026-08-16: "nothing loads". role/tabIndex/keyboard keep it a real control. */}
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDraft(text); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault(); e.stopPropagation(); setDraft(text); setOpen(true);
          }
        }}
        className="inline-flex align-middle mx-1 text-warning hover:opacity-70 cursor-pointer"
        title={k}
        data-testid={`text-edit-pen-${k}`}
      >
        <Pencil className="h-3 w-3" />
      </span>
    </As>
  );
}

/**
 * For text that can only be a plain string — a placeholder, a title, an aria-label. Returns the
 * text unchanged, and in edit mode also a marker for the surrounding element so the field can show
 * that it has an editable attribute. The pen itself cannot go inside the attribute; /admin/text is
 * where those are changed.
 */
export function useEditableAttr(k) {
  const { t } = useTranslation();
  const { editing } = useTextEdit();
  return { text: t(k), marker: editing ? { 'data-i18n-attr-key': k } : {} };
}
